/**
 * Phase 6 Plan 02 — CleaningCadence settings page (CADENCE-6.1; D-138, D-140, D-141).
 *
 * PM-only /settings page. Form anatomy:
 *   <header>
 *     <h1>LABEL_SETTINGS_HEADER</h1>
 *   </header>
 *   <section>
 *     <h2>LABEL_CLEANING_CADENCE_HEADER</h2>
 *     <Banner warning>LABEL_SETTINGS_NETWORK_WIDE_WARNING</Banner>
 *     <NumericField cadence_months min=1 dir='ltr' />
 *     <NumericField warning_days   min=0 dir='ltr' />
 *     <Banner error>{client validation discriminator OR server error code}</Banner>
 *     [Save]   [Reset to defaults]
 *     <Banner success>LABEL_SETTINGS_SAVED_TOAST</Banner>?
 *   </section>
 *
 * Validation discipline (D-138):
 *   - Client-side cadence_months >= 1, warning_days >= 0,
 *     warning_days < cadence_months × 30 (matches Plan 01 server invariants).
 *   - Save disabled when any client validation fails OR pristine OR pending.
 *   - Reset to defaults sets local state to (3, 7); does NOT auto-save —
 *     PM must click Save (D-138 verbatim).
 *   - Numeric inputs use dir='ltr' for Latin-digit anchoring even on AR pages
 *     (REQUIREMENTS.md I18N-9.6 — Latin digits universal).
 *   - Server-side ApiError code maps to ERR_* Banner via mutation.error.code
 *     (the redactor strips Pydantic discriminator strings on the wire because
 *     the path is in CREDENTIAL_PATHS — Plan 01 SUMMARY § two-test split).
 *
 * On Save success → useUpdateCleaningSettings invalidates ASSETS_GEOJSON_KEY
 * (network-wide map recolor) AND CLEANING_SETTINGS_KEY (this page re-renders).
 *
 * @SpecRef("CADENCE-6.1")
 * @SpecRef("6.1")
 */
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "../../components/Button";
import { Banner } from "../../components/Banner";
import { useCleaningSettings } from "./useCleaningSettings";
import { useUpdateCleaningSettings } from "./useUpdateCleaningSettings";

const DEFAULTS = { cadence_months: 3, warning_days: 7 };

interface ValidationResult {
  ok: boolean;
  cadenceErr: string | null;
  warningErr: string | null;
  combinedErr: string | null;
}

function validate(cadence_months: number, warning_days: number): ValidationResult {
  let cadenceErr: string | null = null;
  let warningErr: string | null = null;
  let combinedErr: string | null = null;
  if (!Number.isInteger(cadence_months) || cadence_months < 1) {
    cadenceErr = "ERR_CADENCE_TOO_SHORT";
  }
  if (!Number.isInteger(warning_days) || warning_days < 0) {
    warningErr = "ERR_WARNING_NEGATIVE";
  }
  if (cadenceErr === null && warningErr === null) {
    if (warning_days >= cadence_months * 30) {
      combinedErr = "ERR_WARNING_GTE_CADENCE";
    }
  }
  const ok = cadenceErr === null && warningErr === null && combinedErr === null;
  return { ok, cadenceErr, warningErr, combinedErr };
}

export function CleaningCadencePage() {
  const intl = useIntl();
  const settingsQuery = useCleaningSettings();
  const updateMutation = useUpdateCleaningSettings();

  const [cadenceMonths, setCadenceMonths] = useState<number>(DEFAULTS.cadence_months);
  const [warningDays, setWarningDays] = useState<number>(DEFAULTS.warning_days);
  const [isInit, setIsInit] = useState(false);

  // Sync from server data on first load only — subsequent re-fetches via
  // mutation onSuccess update the cache; we keep the form local thereafter
  // unless the user explicitly Resets.
  useEffect(() => {
    if (settingsQuery.data && !isInit) {
      setCadenceMonths(settingsQuery.data.cadence_months);
      setWarningDays(settingsQuery.data.warning_days);
      setIsInit(true);
    }
  }, [settingsQuery.data, isInit]);

  const validation = validate(cadenceMonths, warningDays);
  const pristine =
    settingsQuery.data !== undefined &&
    settingsQuery.data.cadence_months === cadenceMonths &&
    settingsQuery.data.warning_days === warningDays;
  const saveDisabled =
    !validation.ok || pristine || updateMutation.isPending;

  const handleSave = () => {
    if (!validation.ok || pristine) return;
    updateMutation.mutate({
      cadence_months: cadenceMonths,
      warning_days: warningDays,
    });
  };

  const handleReset = () => {
    setCadenceMonths(DEFAULTS.cadence_months);
    setWarningDays(DEFAULTS.warning_days);
  };

  const serverErrorCode = updateMutation.isError
    ? (updateMutation.error?.code ?? "ERR_GENERIC")
    : null;

  return (
    <div className="ps-8 pe-8 pbs-8 pbe-8" data-testid="cleaning-cadence-page">
      <header className="mbe-6">
        <h1 className="text-2xl font-semibold text-on-surface">
          <FormattedMessage id="LABEL_SETTINGS_HEADER" />
        </h1>
      </header>

      {/* FIND-15-010 (2026-05-11): use max-w-2xl, NOT max-w-xl. Phase 8 brand
        tokens redefine --spacing-xl=32px in tokens.css; Tailwind v4 derives
        max-w-* from --spacing-* when the brand token is present, so
        max-w-xl collapses the section to 32 px instead of 36 rem. The
        xs/sm/md/lg/xl/xxl names are reserved for the brand spacing scale —
        use Tailwind's own 2xl/3xl/4xl scale (or arbitrary [Nrem]) for
        widths. */}
      <section
        data-testid="cleaning-cadence-pane"
        className="bg-surface-raised border border-divider rounded-md ps-6 pe-6 pbs-6 pbe-6 max-w-2xl flex flex-col gap-4"
      >
        <h2 className="text-xl font-semibold text-on-surface">
          <FormattedMessage id="LABEL_CLEANING_CADENCE_HEADER" />
        </h2>

        <Banner variant="warning" data-testid="settings-network-wide-warning">
          <FormattedMessage id="LABEL_SETTINGS_NETWORK_WIDE_WARNING" />
        </Banner>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="cadence-months-input"
            className="text-sm font-semibold text-on-surface"
          >
            <FormattedMessage id="LABEL_CADENCE_MONTHS" />
          </label>
          <input
            id="cadence-months-input"
            data-testid="cadence-months-input"
            type="number"
            inputMode="numeric"
            dir="ltr"
            min={1}
            value={cadenceMonths}
            onChange={(e) => setCadenceMonths(Number(e.target.value))}
            className="h-10 w-full bg-surface-raised border border-divider rounded-md ps-3 pe-3 text-base"
            aria-label={intl.formatMessage({ id: "LABEL_CADENCE_MONTHS" })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="warning-days-input"
            className="text-sm font-semibold text-on-surface"
          >
            <FormattedMessage id="LABEL_WARNING_DAYS" />
          </label>
          <input
            id="warning-days-input"
            data-testid="warning-days-input"
            type="number"
            inputMode="numeric"
            dir="ltr"
            min={0}
            value={warningDays}
            onChange={(e) => setWarningDays(Number(e.target.value))}
            className="h-10 w-full bg-surface-raised border border-divider rounded-md ps-3 pe-3 text-base"
            aria-label={intl.formatMessage({ id: "LABEL_WARNING_DAYS" })}
          />
        </div>

        {validation.cadenceErr && (
          <Banner variant="error" data-testid="cadence-error-banner">
            <FormattedMessage id={validation.cadenceErr} />
          </Banner>
        )}
        {validation.warningErr && (
          <Banner variant="error" data-testid="warning-error-banner">
            <FormattedMessage id={validation.warningErr} />
          </Banner>
        )}
        {validation.combinedErr && (
          <Banner variant="error" data-testid="warning-gte-cadence-banner">
            <FormattedMessage id={validation.combinedErr} />
          </Banner>
        )}
        {serverErrorCode && (
          <Banner variant="error" data-testid="server-error-banner">
            <FormattedMessage id={serverErrorCode} />
          </Banner>
        )}

        {updateMutation.isSuccess && (
          <Banner variant="success" data-testid="settings-saved-toast">
            <FormattedMessage id="LABEL_SETTINGS_SAVED_TOAST" />
          </Banner>
        )}

        <div className="flex justify-end gap-3 mbs-2">
          <Button
            variant="secondary"
            onClick={handleReset}
            disabled={updateMutation.isPending}
            data-testid="cleaning-cadence-reset"
          >
            <FormattedMessage id="LABEL_RESET_TO_DEFAULTS" />
          </Button>
          <Button
            variant="primary"
            type="submit"
            onClick={handleSave}
            disabled={saveDisabled}
            data-testid="cleaning-cadence-save"
          >
            <FormattedMessage id="LABEL_SAVE" />
          </Button>
        </div>
        <p className="text-xs text-text-secondary mbs-1">
          <FormattedMessage id="LABEL_SETTINGS_RESET_HINT" />
        </p>
      </section>
    </div>
  );
}
